import { createRouter, createWebHistory } from 'vue-router';
import { defineComponent, watch } from 'vue';
import type { Persona, UserRole } from '@/types/parley';
import { useAuthStore } from '@/stores/authStore';
import { fetchKnowledgeBaseEnabled } from '@/common/composables/useKnowledgeBaseFlag';
import { fetchChatBalanceCheckEnabled } from '@/common/composables/useChatBalanceCheckFlag';
import { useAccountsStore } from '@/stores/accountsStore';

declare module 'vue-router' {
  interface RouteMeta {
    requiredRole?: UserRole;
    // When true, the route is a pure write flow (e.g. the create-account form)
    // with nothing to view. The view-only `support` role passes the
    // requiredRole level check (it shares super_admin's view level) but is
    // bounced from these by the guard below.
    writeOnly?: boolean;
    // When true, only a *true* `super_admin` may view this route. The view-only
    // `support` role shares super_admin's level (so `requiredRole` alone admits
    // it), but Configuration and its sub-pages are super-admin-only management
    // surfaces support must not see — this flag bounces it to Accounts.
    superAdminOnly?: boolean;
    // When true, the view-only `support` role is excluded from this route even
    // though it shares super_admin's view level. Unlike `superAdminOnly`, admins
    // and super_admins are still allowed — this is for surfaces support alone is
    // scoped out of, like the platform Dashboard. Bounced to Accounts.
    notForSupport?: boolean;
    // Names a runtime feature switch (SSM-backed, flipped by a super-admin on
    // the Configuration page) that must be ON for this route to be reachable.
    // Independent of role: the feature being off is a platform-level fact, so
    // it bounces staff and account owners alike. Hiding the tab button is not
    // enough on its own — a deep link, a bookmark or a stale open tab all
    // reach the URL directly.
    requiresFeature?: 'knowledgeBase' | 'chatBalanceCheck';
    // Where to land when `requiresFeature` is off. Names the route this one is
    // a tab of, so a blocked deep link keeps the account context the caller
    // asked for and simply shows no such tab — rather than ejecting them to
    // their persona landing, which reads as "you were kicked out". The `:id`
    // param is carried over. Falls back to landingFor() when absent.
    featureOffFallback?: string;
    backRoute?: string;
    breadcrumb?: string;
    // When true, KeyUsagePage uses the admin (`getAccount`) fetch path
    // instead of the owner-scoped (`getMyAccount`) path. Required for
    // routes that admins reach via /accounts/:id/... where they may not
    // own the account themselves.
    adminScoped?: boolean;
  }
}

// Maps primaryPersona → landing route name. Used by the `/` beforeEnter so
// users land on the dashboard that matches what they can actually see.
const PERSONA_LANDING: Record<Persona, string> = {
  super_admin: 'Dashboard',
  support: 'Accounts',
  admin: 'Dashboard',
  account_admin: 'MyKeys',
  key_holder: 'MyKeys',
  none: 'NoAccess',
};

// Empty placeholder for the `/` route. `beforeEnter` always redirects, so
// this component never actually renders. A `redirect` field would be simpler
// but can't be async — and we need to await `roleLoaded` before picking the
// persona target, or a still-loading super_admin lands on `/my-keys` instead
// of `/dashboard`.
const HomeRedirect = defineComponent({ render: () => null });

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeRedirect,
      beforeEnter: async () => {
        const authStore = useAuthStore();
        await waitForRoleLoaded(authStore);
        const target = PERSONA_LANDING[authStore.primaryPersona] ?? 'Dashboard';
        return { name: target };
      },
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/features/dashboard/pages/DashboardPage.vue'),
      // requiredRole was missing here, making this the one staff route an
      // ordinary user could deep-link to. It is admin+ (notForSupport still
      // carves support out on top of that).
      meta: { requiredRole: 'admin', notForSupport: true, breadcrumb: 'Dashboard' },
    },
    {
      // Legacy path — now redirects to the key-holder dashboard.
      path: '/my-account',
      redirect: { name: 'MyKeys' },
    },
    {
      // Key-holder dashboard: lists API keys issued to the caller across all
      // accounts, with per-key usage metrics. Refactored from the old
      // UserDashboardPage which conflated this with an owner view.
      path: '/my-keys',
      name: 'MyKeys',
      component: () => import('@/features/user-dashboard/pages/UserDashboardPage.vue'),
      meta: { breadcrumb: 'My Keys' },
    },
    {
      // Personal usage view for any key holder. Hosts the daily-trend chart
      // and top-models breakdown that used to live on /my-keys.
      path: '/usage',
      name: 'Usage',
      component: () => import('@/features/usage/pages/UsagePage.vue'),
      meta: { breadcrumb: 'Usage' },
    },
    {
      // Account-admin dashboard: lists accounts the caller owns with per-key
      // usage drill-down.
      path: '/my-accounts',
      name: 'MyAccounts',
      component: () => import('@/features/account-admin/pages/MyAccountsPage.vue'),
      meta: { breadcrumb: 'My Accounts' },
    },
    {
      path: '/my-accounts/request',
      name: 'RequestAccount',
      component: () => import('@/features/account-admin/pages/RequestAccountPage.vue'),
      meta: { breadcrumb: 'Request account' },
    },
    {
      path: '/my-accounts/:id',
      name: 'MyAccountsDetail',
      component: () => import('@/features/account-admin/pages/AccountAdminDetailPage.vue'),
      props: true,
      meta: { breadcrumb: 'Account' },
    },
    {
      // Transactions tab of the owner account detail page — same component,
      // the active tab is derived from the route name so it is deep-linkable
      // and the browser back button switches tabs. No requiredRole, like the
      // rest of /my-accounts/*: the backend enforces ownership on the
      // transactions GET (owner / account-admin / key-holder).
      path: '/my-accounts/:id/transactions',
      name: 'MyAccountsDetailTransactions',
      component: () => import('@/features/account-admin/pages/AccountAdminDetailPage.vue'),
      props: true,
      meta: { breadcrumb: 'Account transactions' },
    },
    {
      // Invoices tab of the owner account detail page — same component, the
      // active tab is derived from the route name. Like transactions, no
      // requiredRole: the backend enforces ownership on the invoices GET
      // (owner / account-admin / key-holder).
      path: '/my-accounts/:id/invoices',
      name: 'MyAccountsDetailInvoices',
      component: () => import('@/features/account-admin/pages/AccountAdminDetailPage.vue'),
      props: true,
      meta: { breadcrumb: 'Account invoices' },
    },
    {
      // route is scoped to
      // the account-admin role, which an owner (or account admin) holds on
      // their own account regardless of platform-staff role.
      path: '/my-accounts/:id/rag',
      name: 'MyAccountsDetailRag',
      component: () => import('@/features/account-admin/pages/AccountAdminDetailPage.vue'),
      props: true,
      meta: {
        requiresFeature: 'knowledgeBase',
        featureOffFallback: 'MyAccountsDetail',
        breadcrumb: 'Account knowledge base',
      },
    },
    {
      // hashedApiKey is the SHA-256 digest of the key — opaque, stable, safe
      // for URLs. Disambiguates between multiple keys issued to the same
      // kerberos without last-4 collision risk.
      path: '/my-accounts/:id/keys/:krbName/:hashedApiKey',
      name: 'MyAccountsKeyUsage',
      component: () => import('@/features/account-admin/pages/KeyUsagePage.vue'),
      props: true,
      meta: { breadcrumb: 'Key usage' },
    },
    {
      // Same KeyUsagePage component, reached from the /my-keys context. The
      // back button reads route.meta.backRoute to return to /my-keys instead
      // of the account-admin detail page.
      path: '/my-keys/:id/:krbName/:hashedApiKey',
      name: 'MyKeyUsage',
      component: () => import('@/features/account-admin/pages/KeyUsagePage.vue'),
      props: true,
      meta: { backRoute: 'MyKeys', breadcrumb: 'Key usage' },
    },
    {
      path: '/no-access',
      name: 'NoAccess',
      component: () => import('@/features/common/pages/NoAccessPage.vue'),
      meta: { breadcrumb: 'No access' },
    },
    {
      path: '/accounts',
      name: 'Accounts',
      component: () => import('@/features/accounts/pages/AccountsListPage.vue'),
      meta: { requiredRole: 'admin', breadcrumb: 'Accounts' },
    },
    {
      path: '/accounts/create',
      name: 'CreateAccount',
      component: () => import('@/features/accounts/pages/AccountCreatePage.vue'),
      meta: { requiredRole: 'super_admin', writeOnly: true, breadcrumb: 'Create account' },
    },
    {
      path: '/accounts/:id',
      name: 'AccountDetail',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: { requiredRole: 'admin', breadcrumb: 'Account' },
    },
    {
      // Usage tab of the account detail page — same component, the active tab
      // is derived from the route name so the tab is deep-linkable and the
      // browser back button switches tabs.
      path: '/accounts/:id/usage',
      name: 'AccountDetailUsage',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: { requiredRole: 'admin', breadcrumb: 'Account usage' },
    },
    {
      // Transactions tab of the account detail page — same component, route
      // name drives the active tab. Deliberately plain requiredRole: 'admin'
      // (no superAdminOnly/notForSupport): the credit history is readable by
      // all staff; posting is gated inside the panel via canPost.
      path: '/accounts/:id/transactions',
      name: 'AccountDetailTransactions',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: { requiredRole: 'admin', breadcrumb: 'Account transactions' },
    },
    {
      // Invoices tab of the staff account detail page — same component, route
      // name drives the active tab. Plain requiredRole: 'admin' like the
      // transactions tab: invoices are readable by all staff.
      path: '/accounts/:id/invoices',
      name: 'AccountDetailInvoices',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: { requiredRole: 'admin', breadcrumb: 'Account invoices' },
    },
    {
      // Sources (RAG) tab of the staff account detail page — same component,
      // route name drives the active tab. Plain requiredRole: 'admin' like
      // the other tabs. The panel itself is a scaffold as of PARL-302 ticket
      // 03; real source list/forms/sync land in tickets 04-08.
      path: '/accounts/:id/rag',
      name: 'AccountDetailRag',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: {
        requiredRole: 'admin',
        requiresFeature: 'knowledgeBase',
        featureOffFallback: 'AccountDetail',
        breadcrumb: 'Account knowledge base',
      },
    },
    {
      // Other tab of the staff account detail page — same component, route
      // name drives the active tab. Home for the low-traffic, super-admin-only
      // switches that don't belong beside the day-to-day governance cards on
      // Management; Data retention (ADR-039) is the first of them. Gated the
      // way Configuration is: requiredRole turns admins away, superAdminOnly
      // turns away view-only support, which shares super_admin's view level.
      path: '/accounts/:id/other',
      name: 'AccountDetailOther',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Account other settings',
      },
    },
    {
      // Audit tab of the staff account detail page (parley-api ADR-051). The
      // API is super_admin only because the trail shows what other staff did,
      // so view-only support is turned away here too.
      path: '/accounts/:id/audit',
      name: 'AccountDetailAudit',
      component: () => import('@/features/accounts/pages/AccountDetailPage.vue'),
      props: true,
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Account audit trail',
      },
    },
    {
      // Admin-scoped per-key usage view. Reuses KeyUsagePage but flips the
      // account fetch path to the admin endpoint via `adminScoped` so admins
      // can drill into keys on accounts they don't own.
      path: '/accounts/:id/keys/:krbName/:hashedApiKey',
      name: 'AccountsKeyUsage',
      component: () => import('@/features/account-admin/pages/KeyUsagePage.vue'),
      props: true,
      meta: {
        requiredRole: 'admin',
        adminScoped: true,
        backRoute: 'AccountDetail',
        breadcrumb: 'Key usage',
      },
    },
    {
      // Section entry point. The sidebar points here rather than at
      // /billing/runs so its prefix-based active check (AppSidebar.isActive)
      // keeps the Billing row highlighted on every tab and drill-down beneath
      // it, not just the Runs one. Carries the same gate as its target so a
      // non-super_admin is turned away here rather than one redirect later.
      path: '/billing',
      redirect: '/billing/runs',
      meta: { requiredRole: 'super_admin', superAdminOnly: true },
    },
    {
      // The whole billing surface is super-admin-only: menu, both routes, and
      // the backend GET endpoints. superAdminOnly (on top of requiredRole) is
      // what excludes the view-only support role, which otherwise shares
      // super_admin's view level. The "Run billing" / "Re-run" actions remain
      // super-admin-only and backend-enforced as before.
      path: '/billing/runs',
      name: 'BillingRuns',
      component: () => import('@/features/billing/pages/BillingRunsPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Billing' },
    },
    {
      path: '/billing/runs/:id',
      name: 'BillingRunDetail',
      component: () => import('@/features/billing/pages/BillingRunDetailPage.vue'),
      props: true,
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Billing run',
        backRoute: 'BillingRuns',
      },
    },
    {
      // Billing preview: a read-only rehearsal of the run. Reached as the
      // "Preview" tab on the Billing page rather than its own sidebar entry, so
      // it keeps its own URL (deep-linkable, and the detail route below can hang
      // off it) while presenting as one section alongside Runs. Same
      // super-admin gate as the rest of Billing: it exposes every account's
      // balance, and the people who act on it are the ones who can run billing
      // for real.
      path: '/billing/previews',
      name: 'BillingPreviews',
      component: () => import('@/features/billing/pages/BillingPreviewPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Billing' },
    },
    {
      path: '/billing/previews/:previewId',
      name: 'BillingPreviewDetail',
      component: () => import('@/features/billing/pages/BillingPreviewDetailPage.vue'),
      props: true,
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Billing preview',
        backRoute: 'BillingPreviews',
      },
    },
    {
      // IST-owner Finance dashboard (ADR-032): actual cost vs recovery. Same
      // super-admin-only gate as Billing — superAdminOnly bounces the view-only
      // support role even though it shares super_admin's view level. Backend
      // /v1/admin/finance/* re-enforces super_admin per route.
      path: '/finance',
      name: 'Finance',
      component: () => import('@/features/finance/pages/FinancePage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Finance' },
    },
    {
      // Same gate as Finance and Billing: super_admin only, with superAdminOnly
      // bouncing the view-only support role. Reports read platform-wide spend
      // and per-account ledger movement, so the backend re-enforces it on every
      // /v1/admin/reports/* route. See docs/specs/0004-super-admin-reporting.md.
      path: '/reports',
      name: 'Reports',
      component: () => import('@/features/reports/pages/ReportsPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Reporting' },
    },
    {
      path: '/reports/cli',
      name: 'CliStats',
      component: () => import('@/features/reports/pages/CliStatsPage.vue'),
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Parley CLI',
        backRoute: 'Reports',
      },
    },
    {
      // Job logs became a Configuration tab. Kept as a redirect rather than
      // deleted: this path is in bookmarks and in links from runbooks and Slack.
      path: '/job-logs',
      redirect: { name: 'ConfigurationJobLogs' },
    },
    {
      path: '/configuration',
      name: 'Configuration',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Configuration' },
    },
    {
      // The remaining Configuration tabs are not pages of their own: same
      // component, selected on route name. Declared as routes so a tab is
      // linkable and survives a refresh.
      path: '/configuration/guardrails',
      name: 'ConfigurationGuardrails',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Guardrails' },
    },
    {
      path: '/configuration/job-logs',
      name: 'ConfigurationJobLogs',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Job logs' },
    },
    {
      // Super-admin shared MIT knowledge base + fleet oversight (PARL-303).
      //
      // Deliberately carries NO `requiresFeature: 'knowledgeBase'`, unlike the
      // two per-account /rag routes. This tab *renders* the knowledge-base
      // kill switch, so gating it on that switch would make the switch
      // unreachable the moment it is turned off. `superAdminOnly` is the gate
      // here; the switch's meaning stays what it has always been — hide the
      // per-account KB tab — not "KB platform off".
      path: '/configuration/knowledge-base',
      name: 'ConfigurationKnowledgeBase',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Knowledge base' },
    },
    {
      // Platform AI operations: which model Parley uses, and which account is
      // charged, when it calls a model on its own behalf. Its own tab rather
      // than a card on General because it is a standing config with a billing
      // consequence, not another switch.
      path: '/configuration/platform-ai',
      name: 'ConfigurationPlatformAi',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Platform AI' },
    },
    {
      // Models and Notifications were pages of their own, linked from General.
      // They are tabs now, on the same paths, so old links still land on them.
      path: '/configuration/models',
      name: 'ConfigurationModels',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Models' },
    },
    {
      path: '/configuration/notification-templates',
      name: 'ConfigurationNotifications',
      component: () => import('@/features/configuration/pages/ConfigurationPage.vue'),
      meta: { requiredRole: 'super_admin', superAdminOnly: true, breadcrumb: 'Notifications' },
    },
    {
      // CLI adoption is usage reporting, not configuration, so it moved under
      // Reporting. The old path stays as a redirect for bookmarks.
      path: '/configuration/cli',
      redirect: { name: 'CliStats' },
    },
    {
      path: '/configuration/notification-templates/:key/edit',
      name: 'NotificationTemplateEdit',
      component: () => import('@/features/configuration/pages/NotificationTemplateEditorPage.vue'),
      meta: {
        requiredRole: 'super_admin',
        superAdminOnly: true,
        breadcrumb: 'Edit template',
        backRoute: 'ConfigurationNotifications',
      },
    },
    {
      // Approval page accessed via email link. No role restriction — any
      // authenticated MIT user who receives the email can approve or deny.
      // The request token in the URL scopes the action server-side.
      path: '/approve/:requestId',
      name: 'CostObjectApproval',
      component: () => import('@/features/requests/pages/CostObjectApprovalPage.vue'),
      props: true,
      meta: { breadcrumb: 'Approve request' },
    },
    {
      path: '/approvals',
      name: 'Approvals',
      component: () => import('@/features/approvals/pages/ApprovalListPage.vue'),
      meta: { requiredRole: 'super_admin', breadcrumb: 'Approvals' },
    },
    {
      // Chat (LibreChat) balance/transaction lookup — a support tool, not a
      // super-admin-only management surface, so requiredRole is 'support'
      // (same ROLE_LEVEL as super_admin) with no superAdminOnly flag, unlike
      // Billing/Finance/Configuration above.
      path: '/chat',
      name: 'Chat',
      component: () => import('@/features/chat/pages/ChatLookupPage.vue'),
      meta: { requiredRole: 'support', breadcrumb: 'Chat', requiresFeature: 'chatBalanceCheck' },
    },
  ],
});

// Role hierarchy for *view* access. `support` shares super_admin's level so it
// can reach every staff/super-admin screen; its inability to *write* is
// enforced separately via authStore.isReadOnly (controls) and the writeOnly
// route guard below — not by this ranking.
const ROLE_LEVEL: Record<string, number> = {
  super_admin: 3,
  support: 3,
  admin: 2,
  user: 1,
};

// Resolves once the backend `/v1/auth/me` call has populated the store. The
// guard must wait for this on first navigation — otherwise it sees the default
// `role: 'user'` and bounces admins away from /accounts/:id and similar.
function waitForRoleLoaded(authStore: ReturnType<typeof useAuthStore>): Promise<void> {
  if (authStore.roleLoaded) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const stop = watch(
      () => authStore.roleLoaded,
      (loaded) => {
        if (loaded) {
          stop();
          resolve();
        }
      }
    );
  });
}

// Where to send a caller who fails a gate. Their own persona landing, never a
// fixed staff route: the old fallback was a hard-coded `Dashboard`, which sent
// an ordinary user who deep-linked a staff URL straight *into* the platform
// dashboard. Every PERSONA_LANDING target is either ungated or reachable at
// that persona's level, so this cannot loop.
function landingFor(authStore: ReturnType<typeof useAuthStore>) {
  return { name: PERSONA_LANDING[authStore.primaryPersona] ?? 'NoAccess' };
}

router.beforeEach(async (to) => {
  const {
    requiredRole,
    writeOnly,
    superAdminOnly,
    notForSupport,
    requiresFeature,
    featureOffFallback,
  } = to.meta;
  // Skip the (async) role wait entirely for routes with no gating metadata.
  if (!requiredRole && !writeOnly && !superAdminOnly && !notForSupport && !requiresFeature)
    return true;

  const authStore = useAuthStore();
  await waitForRoleLoaded(authStore);

  // The view-only `support` role is scoped to the Accounts surfaces (plus its
  // own personal pages). It shares super_admin's view level but is explicitly
  // excluded from the platform Dashboard / Configuration — send
  // it to the Accounts list it can browse. Checked before requiredRole because
  // support passes the level check (it shares super_admin's level), so the
  // level check alone would let it through.
  if (notForSupport && authStore.isSupport) {
    return { name: 'Accounts' };
  }

  if (requiredRole) {
    const userLevel = ROLE_LEVEL[authStore.role] ?? 0;
    const requiredLevel = ROLE_LEVEL[requiredRole] ?? 0;
    if (userLevel < requiredLevel) {
      return landingFor(authStore);
    }
  }

  // Write-only flows (create account) have nothing for a view-only role to
  // see — send support back to the list it can browse.
  if (writeOnly && authStore.isReadOnly) {
    return { name: 'Accounts' };
  }

  // Configuration / Allocations are super-admin-only management surfaces. The
  // view-only `support` role shares super_admin's view level, so block it here
  // explicitly (deep-link protection — the nav item is already hidden).
  if (superAdminOnly && !authStore.isSuperAdmin) {
    return { name: 'Accounts' };
  }

  // Runtime feature switches, checked LAST — after every role gate. A caller
  // who has no business on this route at all is answered by the role checks
  // above, so they never reach the fallback below and get bounced twice ("you
  // may not see staff pages" must win over "this feature is off"). A caller who
  // passed the role gate lands on the route this one is a tab of, with the tab
  // simply absent; only a feature-gated route with no fallback named falls back
  // to the persona landing.
  if (requiresFeature === 'knowledgeBase' && !(await fetchKnowledgeBaseEnabled())) {
    return featureOffFallback
      ? { name: featureOffFallback, params: { id: to.params.id } }
      : landingFor(authStore);
  }

  if (requiresFeature === 'chatBalanceCheck' && !(await fetchChatBalanceCheckEnabled())) {
    return featureOffFallback
      ? { name: featureOffFallback, params: { id: to.params.id } }
      : landingFor(authStore);
  }

  return true;
});

router.afterEach((to) => {
  if (!to.path.startsWith('/accounts')) {
    useAccountsStore().resetFilters();
  }
});

// Recover from stale-chunk failures after a deploy. Vite emits content-hashed
// chunk filenames; the previous ones are pruned from S3 on deploy. An
// already-loaded SPA still has the old paths in its lazy-import map, so a
// route change tries to import a chunk that no longer exists. CloudFront's
// SPA fallback rewrites the 404 to index.html, so the browser gets HTML with
// a text/html MIME type and the module load rejects with one of the errors
// matched below. A hard navigation to `to.fullPath` re-fetches index.html
// (which has the new hash map) and continues to the user's intended route.
router.onError((error, to) => {
  const msg = (error as Error | undefined)?.message ?? '';
  const isStaleChunk =
    /Failed to fetch dynamically imported module/.test(msg) ||
    /error loading dynamically imported module/.test(msg) ||
    /Importing a module script failed/.test(msg);
  if (isStaleChunk) {
    window.location.href = to.fullPath;
  }
});

export default router;
